/**
 * Step 6: Delivery Configuration
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Loader2, ChevronLeft, Clock, Truck, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeliveryConfigStep({ data, onComplete, onBack }) {
  const [formData, setFormData] = useState({
    prep_time_minutes: data?.prep_time_minutes || 30,
    pickup_instructions: data?.pickup_instructions || '',
    delivery_radius_miles: data?.delivery_radius_miles || 5.0,
    accepts_delivery: data?.accepts_delivery ?? true,
    accepts_pickup: data?.accepts_pickup ?? true
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.accepts_delivery && !formData.accepts_pickup) {
      toast.error('You must accept at least delivery or pickup orders');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/retailer/onboarding/delivery-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Delivery configuration saved!');
        onComplete(result);
      } else {
        const errors = result.detail?.errors || [result.detail];
        errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error('Failed to save delivery configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Prep Time */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-600" />
          <Label className="text-base font-medium">Order Preparation Time</Label>
        </div>
        <p className="text-sm text-gray-500">
          How long does it typically take to prepare an order for pickup/delivery?
        </p>
        
        <div className="flex items-center gap-4">
          <Slider
            value={[formData.prep_time_minutes]}
            onValueChange={([value]) => handleChange('prep_time_minutes', value)}
            min={5}
            max={120}
            step={5}
            className="flex-1"
            data-testid="prep-time-slider"
          />
          <div className="w-24 text-right">
            <span className="text-2xl font-bold text-emerald-600">{formData.prep_time_minutes}</span>
            <span className="text-sm text-gray-500 ml-1">min</span>
          </div>
        </div>
        
        <div className="flex justify-between text-xs text-gray-400">
          <span>5 min</span>
          <span>60 min</span>
          <span>120 min</span>
        </div>
      </div>

      {/* Order Types */}
      <div className="space-y-4">
        <Label className="text-base font-medium">Accepted Order Types</Label>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium">Delivery Orders</p>
                <p className="text-sm text-gray-500">Orders delivered via DoorDash</p>
              </div>
            </div>
            <Switch
              checked={formData.accepts_delivery}
              onCheckedChange={(checked) => handleChange('accepts_delivery', checked)}
              data-testid="accepts-delivery-switch"
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium">Pickup Orders</p>
                <p className="text-sm text-gray-500">Customers pick up from your store</p>
              </div>
            </div>
            <Switch
              checked={formData.accepts_pickup}
              onCheckedChange={(checked) => handleChange('accepts_pickup', checked)}
              data-testid="accepts-pickup-switch"
            />
          </div>
        </div>
      </div>

      {/* Pickup Instructions */}
      {formData.accepts_pickup && (
        <div className="space-y-2">
          <Label htmlFor="pickup_instructions">Pickup Instructions (Optional)</Label>
          <Textarea
            id="pickup_instructions"
            value={formData.pickup_instructions}
            onChange={(e) => handleChange('pickup_instructions', e.target.value)}
            placeholder="E.g., 'Enter through side door', 'Ask for pickup at customer service desk'"
            rows={3}
            data-testid="pickup-instructions-textarea"
          />
          <p className="text-xs text-gray-500">
            Help customers know where and how to pick up their orders
          </p>
        </div>
      )}

      {/* Delivery Radius */}
      {formData.accepts_delivery && (
        <div className="space-y-4">
          <Label className="text-base font-medium">Delivery Radius</Label>
          <p className="text-sm text-gray-500">
            Maximum distance for delivery orders (DoorDash may adjust based on availability)
          </p>
          
          <div className="flex items-center gap-4">
            <Slider
              value={[formData.delivery_radius_miles]}
              onValueChange={([value]) => handleChange('delivery_radius_miles', value)}
              min={1}
              max={15}
              step={0.5}
              className="flex-1"
              data-testid="delivery-radius-slider"
            />
            <div className="w-24 text-right">
              <span className="text-2xl font-bold text-emerald-600">{formData.delivery_radius_miles}</span>
              <span className="text-sm text-gray-500 ml-1">mi</span>
            </div>
          </div>
        </div>
      )}

      {/* Warning if neither selected */}
      {!formData.accepts_delivery && !formData.accepts_pickup && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          You must accept at least one order type (delivery or pickup)
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={saving || (!formData.accepts_delivery && !formData.accepts_pickup)}
          className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
          data-testid="save-delivery-config-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </Button>
      </div>
    </form>
  );
}
